import { Field } from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import { FormLabel } from "../../shared/form-label/FormLabel.js";
import type { TooltipPositionProps } from "../tooltip/Tooltip.js";

interface TextAreaProps extends React.ComponentPropsWithoutRef<"textarea"> {
  label?:
    | string
    | {
        label?: string;
        optional?: boolean;
        infoMessage?: string;
        tooltipPosition?: TooltipPositionProps;
      };
  helperText?: HelperTextProps;
  error?: boolean | string;
  classNames?: {
    root?: string;
    label?: string;
    textarea?: string;
    helperText?: string;
  };
  size?: "xs" | "sm" | "md" | "lg";
}

const TextArea = React.forwardRef<React.ElementRef<"textarea">, TextAreaProps>(
  (props, ref) => {
    const defaultId = React.useId();
    const {
      id = defaultId,
      className,
      classNames,
      label,
      size = "md",
      error,
      helperText = { message: "", status: "default" },
      ...rest
    } = props;
    const ariaDescribedBy =
      props["aria-describedby"] ?? `description-${defaultId}`;

    return (
      <div
        className={clsx(
          "ox-reset ox-TextAreaRoot",
          className,
          classNames?.root,
        )}
        data-size={size}
        data-invalid={!!error}
      >
        <FormLabel
          label={label}
          size={size}
          id={id}
          htmlFor={id}
          error={error}
        />

        <textarea
          ref={ref}
          className={clsx("ox-TextArea", classNames?.textarea, className)}
          id={id}
          data-invalid={!!error}
          aria-describedby={helperText || error ? ariaDescribedBy : undefined}
          data-size={size}
          {...rest}
        />

        {helperText?.message && (
          <Field.HelperText
            data-size={size}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
            data-invalid={helperText?.status === "error" || !!error}
          >
            {getFormHelperText(helperText, size)}
          </Field.HelperText>
        )}
      </div>
    );
  },
);

TextArea.displayName = "TextArea";
export { TextArea };
export type { TextAreaProps };
